import i18n from 'i18next';
import { createApiAuthenticated, removeTokenFromApi, getAPI } from 'src/utils/api';
import { notification } from 'antd';

import checkAuthError from '../utils/checkAuthError';

import store from 'src/redux/store/reduxStoreIndex';
import { getActiveUserRedux } from 'src/redux/actions/activeUserActions';
import { deleteTokenLocal, getLocalToken, storeTokenLocal } from '../indexeddb/cookieIdb';

notification.config({
	placement: 'topRight',
	top: 75,
	duration: 2,
});
const getAllUsers = async () => {
	try {
		const api = await getAPI();
		const response = await api.get('/user/all');
		return response;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const addNewUser = async (newUser) => {
	try {
		const api = await getAPI();
		const { data } = await api.post('user/new', { ...newUser, hostName: window.location.host });
		notification.success({
			message: i18n.t('user-container.added'),
		});
		return data;
	} catch (error) {
		checkAuthError(error);

		notification.error({
			message: i18n.t('notifications.onSaveError'),
			description: error.message,
		});
		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};
const deleteUser = async (userId) => {
	try {
		const api = await getAPI();
		const { data } = await api.delete(`user/${userId}`);
		notification.success({
			message: i18n.t('user-container.deleted'),
		});
		return data;
	} catch (error) {
		checkAuthError(error);

		const errorMessage =
			error.message === 'you cannot delete the last admin'
				? i18n.t('notifications.onDeleteUserAdminErrorMessage')
				: i18n.t('notifications.onDeleteUserErrorMessage');
		notification.error({
			message: i18n.t('notifications.onSaveError'),
			description: errorMessage,
		});
		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const updateUser = async (userObject) => {
	try {
		const api = await getAPI();
		const { data } = await api.put(`user/update`, {
			_id: userObject._id,
			userObject,
		});

		notification.success({
			message: i18n.t('notifications.onUpdateUserAsAdminOk'),
		});
		return data;
	} catch (error) {
		checkAuthError(error);

		notification.error({
			message: i18n.t('notifications.onUpdateUserSelfError'),
			description: error.message,
		});
		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const updateUserLanguage = async (userId, language) => {
	try {
		const api = await getAPI();
		const { data } = await api.post('/user/self/language', {
			_id: userId,
			language,
		});

		notification.success({
			message: i18n.t(['common:notifications.onUpdateUserSelfOk', '*Configuration updated']),
			description: i18n.t(['common:AlertChangeSuccess', '*Changes applied']),
		});

		const { user } = data;
		return user;
	} catch (error) {
		checkAuthError(error);

		notification.error({
			message: i18n.t('common:notifications.onUpdateUserSelfError'),
			description: `${error.response.data.error + error.status} ${error.statusText}`,
		});
		if (error.response.data.message) {
			console.error(error.response.data.message);

			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};
const updateUserPassword = async (userId, oldPassword, newPassword) => {
	try {
		const api = await getAPI();
		await api.post('/user/self/password', {
			_id: userId,
			oldPassword,
			newPassword,
		});
	} catch (error) {
		checkAuthError(error);

		notification.error({
			message: i18n.t('common:notifications.onUpdatePasswordError'),
			description:
				error.response.data.message === 'Password not correct'
					? i18n.t('common:notifications.wrongPassword')
					: error.response.data.message,
		});
		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};
const updateUserAvatar = async (userId, avatar) => {
	try {
		const api = await getAPI();
		const { data } = await api.put('/user/self/avatar', {
			_id: userId,
			avatar,
		});

		notification.success({
			message: i18n.t(['common:notifications.onUpdateUserSelfOk', '*Configuration updated']),
			description: i18n.t(['common:AlertChangeSuccess', '*Changes applied']),
		});

		const { user } = data;
		return user;
	} catch (error) {
		checkAuthError(error);

		notification.error({
			message: i18n.t('common:notifications.onUpdateUserSelfError'),
			description: `${error.response.data.error + error.status} ${error.statusText}`,
		});
		if (error.response.data.message) {
			console.error(error.response.data.message);

			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const authenticateUser = async (name = null, password = null) => {
	try {
		const api = await getAPI();
		// authenticate user
		const { data } = await api.post('/user/auth', {
			user: {
				name,
				password,
			},
		});
		const { user, token } = data;

		i18n.changeLanguage(user.language);

		// set session cookie
		await storeTokenLocal(`Token ${token}`);
		// create authenticated api (set token in headers)
		await createApiAuthenticated(`Token ${token}`);
		return { user, token };
	} catch (error) {
		checkAuthError(error);

		let errorMessage = i18n.t('login.passOrUsername');
		if (error.response.data.message === 'ldap user') {
			errorMessage = i18n.t('login.ldapAttemptAsNormal');
		}
		if (error.response.data.message === 'password: doesnt exist') {
			errorMessage = i18n.t('login.notRegistered');
		}
		throw new Error(errorMessage);
	}
};
const authenticateLdapUser = async (username = null, password = null) => {
	try {
		// authenticate user
		const api = await getAPI();
		const { data } = await api.post('/user/auth/ldap', {
			username,
			password,
		});
		// receive session data
		const { user, token, message } = data;

		if (message === 'not whitelisted') {
			throw new Error(i18n.t('login.nowh', 'You are not whitelisted'));
		} else if (message === 'ladap not activated') {
			throw new Error(i18n.t('login.nlda', 'ldap not activated'));
		}
		// set session cookie
		await storeTokenLocal(`Token ${token}`);
		// create authenticated api (set token in headers)
		await createApiAuthenticated(`Token ${token}`);
		return { user, token };
	} catch (error) {
		checkAuthError(error);
		if (error.response.data.message) {
			let errorMessage = error.response.data.message;
			if (error.response.data.message === 'user blacklisted') {
				errorMessage = i18n.t('login.blac', 'You are blacklisted');
			} else if (error.response.data.message === 'no such ldap user found') {
				errorMessage = i18n.t('login.noUser', '*This user does not exist');
			} else if (error.response.data.message === 'ldap auth failed') {
				errorMessage = i18n.t('login.wrongPassword', '*Incorrect Password');
			}
			throw new Error(errorMessage);
		} else if (error === 'user blacklisted') {
			throw new Error(i18n.t('login.blac', 'You are blacklisted'));
		}
	}
};
const logout = async (userId) => {
	try {
		if (userId) {
			const api = await getAPI();
			// send request to log out user if the user is known
			await api.delete(`/user/logout`);
		}
		await deleteTokenLocal();

		removeTokenFromApi();

		notification.success({
			message: i18n.t('logout.success', 'Logout successful'),
		});
	} catch (error) {
		console.log(error);
		const errorMessage = error?.response?.data?.message ? error.response.data.message : error;
		notification.error({
			message: i18n.t('logout.failure', 'Logout failure'),
			description: errorMessage,
		});
	} finally {
		// it is pretty important that we logout on client and not so important that we logout on server, so if something crashes we still want to do it
		await removeTokenFromApi();
		deleteTokenLocal().catch((err) => console.log('logout() deleteTokenLocal', err));
		i18n.changeLanguage('en');
	}
};

const checkUser = async () => {
	try {
		const token = await getLocalToken();
		if (token) {
			await createApiAuthenticated(token);
			// get active user and store in redux store
			store.dispatch(getActiveUserRedux());
			return true;
		} else {
			return false;
		}
	} catch (error) {
		checkAuthError(error);

		console.error(error);
		throw error;
	}
};

const getActiveUser = async () => {
	try {
		// get active user (this only works if the correct token is set in the api)
		const api = await getAPI();
		const { data } = await api.get('user/active');

		const { activeUser } = data;
		i18n.changeLanguage(activeUser.language);

		return activeUser;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error('No active user at api found', error.response.data.message);
			throw error.response.data.message;
		} else {
			console.error('No active user at api found', error);
			throw error;
		}
	}
};

const getRegistration = async (id) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`user/setPassword/${id}`);
		return data?.registrationUser;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			throw new Error(error.response.data.message);
		} else {
			throw error;
		}
	}
};

const setNewPassword = async (username, password, id) => {
	try {
		const api = await getAPI();
		const { data } = await api.post(`user/setPassword`, {
			name: username,
			password,
			currentUri: id,
		});
		return data;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			throw new Error(error.response.data.message);
		} else {
			throw error;
		}
	}
};

const deletePassword = async (username) => {
	try {
		const api = await getAPI();
		const { data } = await api.delete(`user/password/${username}`, {
			data: {
				username,
				hostname: window.location.host,
			},
		});
		if (data?.success) {
			notification.success({
				message: i18n.t('user-container.resetSucc'),
			});
			return true;
		}
		throw Error;
	} catch (error) {
		checkAuthError(error);
		notification.error({
			message: i18n.t('user-container.resetFail'),
			description: error.response.data.message,
		});
	}
};

const requestNewMail = async (userid, newMail) => {
	try {
		const api = await getAPI();
		const { data } = await api.post(`user/self/setNewMail`, {
			_id: userid,
			newMail,
			hostname: window.location.host,
		});
		return data;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			throw new Error(error.response.data.message);
		} else {
			throw error;
		}
	}
};

const confirmNewMail = async (uri) => {
	try {
		const api = await getAPI();
		const { data } = await api.post(`user/confirmNewMail`, {
			uri,
		});

		if (data?.success) {
			notification.success({
				message: i18n.t('common:notifications.confirmNewMailSuccess'),
				description: data?.newMailSet
					? i18n.t('common:notifications.confirmNewMailSuccessNewMailSet')
					: i18n.t('common:notifications.confirmNewMailSuccessNotNewMailSet'),
			});
		} else {
			throw new Error('We dont know either what went wrong, but something went wrong.');
		}

		return data;
	} catch (error) {
		checkAuthError(error);

		const message = error.response.data.message ? error.response.data.message : error.message;
		notification.error({
			message: i18n.t('common:notifications.confirmNewMailError'),
			description: message,
		});
	}
};
export {
	getAllUsers,
	addNewUser,
	deleteUser,
	updateUser,
	authenticateUser,
	logout,
	authenticateLdapUser,
	updateUserLanguage,
	updateUserPassword,
	checkUser,
	getActiveUser,
	getRegistration,
	setNewPassword,
	deletePassword,
	requestNewMail,
	confirmNewMail,
	updateUserAvatar,
};
